import { useMemo, useState } from 'react';
import type { ConfigResponse, FormPurpose, RolloutDraft, ValidationIssue } from '../types';
import { Button, Card, Notice } from '../components/ui/primitives';
import { CohortsSection } from '../components/review/CohortsSection';
import {
  CampaignSetupSection,
  CommercialsSection,
  IntegrationSection,
  MetricsSection,
  PerformanceSection,
  RecordDetailsSection,
  SetTypeField,
} from '../components/review/sections';
import {
  computeImpressions,
  deriveEndDate,
  errorFor,
  indexIssues,
  updateSlice,
} from '../state/draft';
import { countSelected, setTypeOptions, syncSetType } from '../state/setType';

/**
 * Screen 2 — Review & Edit.
 *
 * Renders the parsed draft in the section layout from the supplied design
 * images and lets the user correct anything before it becomes a document.
 */
export function ReviewEditScreen({
  draft,
  config,
  parseNotes,
  serverIssues,
  submitting,
  purpose = { mode: 'create' },
  onChange,
  onBack,
  onContinue,
}: {
  draft: RolloutDraft;
  config: ConfigResponse;
  parseNotes: string[];
  serverIssues: ValidationIssue[];
  submitting: boolean;
  onChange: (draft: RolloutDraft) => void;
  /**
   * Why the screen is open, which decides the action offered and whether the
   * fields accept input. One rendering of a rollout serves all four cases, so a
   * viewed request cannot look different from an edited one.
   */
  purpose?: FormPurpose;
  onBack: () => void;
  onContinue: () => void;
}) {
  const [notesDismissed, setNotesDismissed] = useState(false);

  const issues = useMemo(() => indexIssues(serverIssues), [serverIssues]);

  const errorList = serverIssues.filter((issue) => issue.severity === 'error');
  const warningList = serverIssues.filter((issue) => issue.severity === 'warning');

  // The end date is always derived, so it cannot contradict the duration.
  const derivedEndDate = useMemo(
    () =>
      deriveEndDate(
        draft.campaign.likelyStartDate,
        draft.campaign.durationValue,
        draft.campaign.durationUnit,
      ),
    [draft.campaign.likelyStartDate, draft.campaign.durationValue, draft.campaign.durationUnit],
  );

  const computedImpressions = useMemo(
    () => computeImpressions(draft.commercials.roValue, draft.commercials.cpm),
    [draft.commercials.roValue, draft.commercials.cpm],
  );

  /** Cohort errors, keyed by index, so a row can highlight itself. */
  const cohortErrors = useMemo(() => {
    const map: Record<number, string | undefined> = {};
    for (const issue of serverIssues) {
      const match = /^cohorts\.(\d+)/.exec(issue.path);
      if (match && issue.severity === 'error') {
        map[Number(match[1])] = issue.message;
      }
    }
    return map;
  }, [serverIssues]);

  const readOnly = purpose.mode === 'view';

  /** What the primary action does, in the operator's words. */
  const action =
    purpose.mode === 'amend'
      ? 'Update the document'
      : purpose.mode === 'duplicate'
        ? 'Continue to destination'
        : 'Continue';

  const banner =
    purpose.mode === 'view'
      ? {
          tone: 'info' as const,
          title: `Viewing ${purpose.tabTitle}`,
          body: 'Read only. Use Edit on the request to change it.',
        }
      : purpose.mode === 'amend'
        ? {
            tone: 'warning' as const,
            title: `Editing ${purpose.tabTitle}`,
            body: 'Saving rewrites this tab from these details. Recorded audience pushes are kept; anything typed into the tab by hand is not.',
          }
        : purpose.mode === 'duplicate'
          ? {
              tone: 'info' as const,
              title: `Duplicated from ${purpose.fromTabTitle}`,
              body: 'Nothing is changed until you continue, and this will create a new tab rather than touching the original.',
            }
          : null;

  return (
    <div className="space-y-5">
      {banner ? (
        <Notice tone={banner.tone} title={banner.title}>
          {banner.body}
        </Notice>
      ) : null}

      {parseNotes.length && !notesDismissed ? (
        <Notice
          tone="info"
          title="A few things to check"
          action={
            <button
              type="button"
              onClick={() => setNotesDismissed(true)}
              className="shrink-0 text-micro text-ink-muted hover:text-ink"
            >
              Dismiss
            </button>
          }
        >
          <ul className="list-disc space-y-1 pl-4">
            {parseNotes.map((note) => (
              <li key={note}>{note}</li>
            ))}
          </ul>
        </Notice>
      ) : null}

      <Card className="p-5 sm:p-7">
        {/*
          A single disabled fieldset makes the whole form read only, rather
          than a flag threaded through ten section components — which would
          be ten chances to miss a control.
        */}
        <fieldset disabled={readOnly} className="min-w-0 space-y-9 disabled:opacity-100">
          <RecordDetailsSection
            record={draft.record}
            businessType={draft.businessType}
            statusOptions={config.statusOptions}
            sources={draft.sources}
            issues={issues}
            onChange={(patch, paths) => onChange(updateSlice(draft, 'record', patch, paths))}
          />

          <CohortsSection
            cohorts={draft.cohorts}
            errors={cohortErrors}
            sectionError={errorFor(issues, 'cohorts')}
            onChange={(cohorts) =>
              onChange({
                ...draft,
                cohorts,
                /*
                 * When Set Type is the per-cohort form its count *is* the number
                 * of selected cohorts, so it has to follow this edit. Any other
                 * value — combined, or wording typed by hand — is left alone.
                 */
                setType: syncSetType(draft.setType, config, countSelected(cohorts)),
                sources: { ...draft.sources, cohorts: 'user' },
              })
            }
          />

          <SetTypeField
            setType={draft.setType}
            options={setTypeOptions(config, countSelected(draft.cohorts))}
            issues={issues}
            onChange={(setType) =>
              onChange({ ...draft, setType, sources: { ...draft.sources, setType: 'user' } })
            }
          />

          <CampaignSetupSection
            campaign={draft.campaign}
            sources={draft.sources}
            issues={issues}
            derivedEndDate={derivedEndDate}
            onChange={(patch, paths) =>
              // The derived end date is written into the draft alongside the
              // change so the submitted payload carries it too.
              onChange(
                updateSlice(
                  draft,
                  'campaign',
                  {
                    ...patch,
                    likelyEndDate: deriveEndDate(
                      patch.likelyStartDate ?? draft.campaign.likelyStartDate,
                      patch.durationValue ?? draft.campaign.durationValue,
                      patch.durationUnit ?? draft.campaign.durationUnit,
                    ),
                  },
                  paths,
                ),
              )
            }
          />

          <CommercialsSection
            commercials={draft.commercials}
            currencySymbol={config.currencySymbol}
            receiptMethods={config.impressionReceiptMethods}
            sources={draft.sources}
            issues={issues}
            computedImpressions={computedImpressions}
            onChange={(patch, paths) => {
              const next = updateSlice(draft, 'commercials', patch, paths);
              // In Auto mode the stored figure always tracks the formula.
              if (next.commercials.impressionMode === 'AUTO') {
                next.commercials = {
                  ...next.commercials,
                  impression: computeImpressions(next.commercials.roValue, next.commercials.cpm),
                };
              }
              onChange(next);
            }}
          />

          <MetricsSection
            metrics={draft.metrics}
            currencySymbol={config.currencySymbol}
            issues={issues}
            onChange={(metrics) => onChange({ ...draft, metrics })}
          />

          <IntegrationSection
            integration={draft.integration}
            config={config}
            sources={draft.sources}
            issues={issues}
            onChange={(patch, paths) => onChange(updateSlice(draft, 'integration', patch, paths))}
          />

          <PerformanceSection />
        </fieldset>
      </Card>

      {errorList.length ? (
        <Notice tone="error" title="Fix these before continuing">
          <ul className="list-disc space-y-0.5 pl-4">
            {errorList.map((issue) => (
              <li key={`${issue.path}-${issue.message}`}>{issue.message}</li>
            ))}
          </ul>
        </Notice>
      ) : null}

      {warningList.length ? (
        <Notice tone="warning" title="Worth a look — these will not block you">
          <ul className="list-disc space-y-0.5 pl-4">
            {warningList.map((issue) => (
              <li key={`${issue.path}-${issue.message}`}>{issue.message}</li>
            ))}
          </ul>
        </Notice>
      ) : null}

      <div className="flex flex-col-reverse gap-3 sm:flex-row sm:justify-end">
        <Button variant="secondary" onClick={onBack}>
          {readOnly ? 'Close' : 'Back'}
        </Button>
        {/* Nothing to submit when the form is only being read. */}
        {readOnly ? null : (
          <Button onClick={onContinue} loading={submitting}>
            {action}
          </Button>
        )}
      </div>
    </div>
  );
}
